import { __assign, __values } from "tslib";
import { getUserActionsDateTime } from './DateUtilities';
import { getFileType, shouldIgnoreFile } from './FileUtilities';
/**
 * Utility function to merge incremental Edgeworth API response into a map with document ID as key
 */
export function mergeAllFilesIntoMap(allFilesMap, newFiles) {
    var e_1, _a;
    var _b, _c, _d, _e, _f;
    var updatedFiles = [];
    var _loop_1 = function (newFile) {
        if (newFile.is_deleted) {
            if (allFilesMap.has(newFile.id)) {
                allFilesMap.delete(newFile.id);
                updatedFiles.push(newFile);
            }
            return "continue";
        }
        if (shouldIgnoreFile(newFile)) {
            return "continue";
        }
        var existingFile = allFilesMap.get(newFile.id);
        var userActionsDate = getUserActionsDateTime(newFile);
        var userActionsDateInMs = userActionsDate === null || userActionsDate === void 0 ? void 0 : userActionsDate.getTime();
        var inverseUserActionsDateInMs = userActionsDateInMs
            ? -userActionsDateInMs
            : 0;
        if (!existingFile) {
            //Don't add meeting objects with the 'Mail' reference_type first, since they will not have subject populated
            if (newFile.reference_type !== 'Mail') {
                var updatedFile = __assign(__assign({}, newFile), { useractions_datetime: userActionsDate, useractions_datetime_inverse_time_in_ms: inverseUserActionsDateInMs });
                allFilesMap.set(newFile.id, updatedFile);
                updatedFiles.push(updatedFile);
            }
        }
        else {
            // TODO: Alternative solution to determining whether to update an existing file, is to mark a file as "synced" if this function is called
            // from delta sync flow. The following solution is prone to broken scenarios if Recent/DeltaSync APIs change.
            var hasNewDeltaSyncProps = (newFile.activities &&
                !existingFile.activities) ||
                (newFile.subject &&
                    !existingFile.subject) ||
                (((_b = newFile.user_relationship) === null || _b === void 0 ? void 0 : _b.last_shared_by_history) &&
                    !((_c = existingFile.user_relationship) === null || _c === void 0 ? void 0 : _c.last_shared_by_history));
            var hasNewerStoreTimestamp = newFile.last_store_modified_datetime >
                existingFile.last_store_modified_datetime;
            var hasNewerRecommendedTimestamp = newFile.last_recommended_modified_datetime >
                existingFile.last_recommended_modified_datetime;
            var shouldUpdate_1 = hasNewerStoreTimestamp ||
                hasNewerRecommendedTimestamp ||
                ((newFile.last_recommended_modified_datetime ===
                    existingFile.last_recommended_modified_datetime ||
                    newFile.last_store_modified_datetime ===
                        existingFile.last_store_modified_datetime) &&
                    hasNewDeltaSyncProps);
            var updatedFile = existingFile;
            /**
             * The following condition is needed when the same file is seen twice in a full sync, where data source is ['Wset'] or ['Wset','Mru']
             * the first time, and 'Mru' the second. When hasMruSource is true, we need to override only the mru-specific properties.
             */
            var hasMruSource = ((_d = newFile.data_source) === null || _d === void 0 ? void 0 : _d.length) > 0 &&
                newFile.data_source.some(function (dataSource) { return dataSource.toLowerCase() === 'mru'; });
            var meetingArtifacts_1 = existingFile.meeting_artifacts;
            if (getFileType(existingFile) === 'MeetingObject') {
                (_e = newFile.meeting_artifacts) === null || _e === void 0 ? void 0 : _e.forEach(function (newArtifact) {
                    var foundArtifact = meetingArtifacts_1 === null || meetingArtifacts_1 === void 0 ? void 0 : meetingArtifacts_1.find(function (artifact) { return newArtifact.id === artifact.id; });
                    if (!foundArtifact && !!meetingArtifacts_1) {
                        meetingArtifacts_1.push(newArtifact);
                        shouldUpdate_1 = true;
                    }
                });
            }
            if (shouldUpdate_1) {
                updatedFile = __assign(__assign(__assign({}, existingFile), newFile), { useractions_datetime: userActionsDate, useractions_datetime_inverse_time_in_ms: inverseUserActionsDateInMs, meeting_artifacts: meetingArtifacts_1 });
                // Overriding SFL datetime explicitly because the property is missing for unpinned items
                updatedFile.user_relationship.save_for_later_datetime =
                    newFile.user_relationship.save_for_later_datetime;
                allFilesMap.set(existingFile.id, updatedFile);
                //Finds deleted meetings -- meetings that are present in the existingFile but not in the newFile
                var deletedMeetingArray = (_f = existingFile.meetings) === null || _f === void 0 ? void 0 : _f.filter(function (meetingId) { var _a; return !((_a = newFile.meetings) === null || _a === void 0 ? void 0 : _a.includes(meetingId)); });
                if (deletedMeetingArray) {
                    deletedMeetingArray.forEach(function (meetingId) {
                        allFilesMap.delete(meetingId);
                    });
                }
            }
            // If new file has Mru as data source, we always override existing Mru-specific properties
            // This is required as a file can potentially be synced twice, once from Wset with a newer timestamp, and once from Mru with older timestamp
            // With existing DeltaSync logic, we would ignore files from older timestamps and leave out the Mru properties, so the overrides below are not
            // included above
            if (hasMruSource) {
                updatedFile.mru_id = updatedFile.mru_id || newFile.mru_id;
                updatedFile.mru_type = updatedFile.mru_type || newFile.mru_type;
                updatedFile.mru_url = updatedFile.mru_url || newFile.mru_url;
                updatedFile.is_pinned =
                    updatedFile.is_pinned || newFile.is_pinned;
            }
            if (shouldUpdate_1 || hasMruSource) {
                updatedFiles.push(updatedFile);
            }
        }
    };
    try {
        for (var newFiles_1 = __values(newFiles), newFiles_1_1 = newFiles_1.next(); !newFiles_1_1.done; newFiles_1_1 = newFiles_1.next()) {
            var newFile = newFiles_1_1.value;
            _loop_1(newFile);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (newFiles_1_1 && !newFiles_1_1.done && (_a = newFiles_1.return)) _a.call(newFiles_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return updatedFiles;
}
//# sourceMappingURL=mergeAllFilesIntoMap.js.map